import React, { useState, useMemo, useEffect } from "react";
import { useTranslation } from "react-i18next";

import {
  Dialog,
  DialogContent,
  DialogTitle,
  Grid,
  useTheme,
  Box,
  Button,
  CircularProgress,
  Alert,
  Snackbar,
  Tooltip,
  IconButton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import LabelIcon from "@mui/icons-material/LabelOutlined";
import AutoAwesomeOutlinedIcon from "@mui/icons-material/AutoAwesomeOutlined";
import SegmentOutlinedIcon from "@mui/icons-material/SegmentOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { getProticoClient } from "../../../utils/api";
import "./index.css";
import { useAppDispatch, useAppSelector } from "../../../hooks";
import {
  saveProfilePicture,
  saveProfilePictureFromUrl,
} from "../../../store/reducers/profile";
import useBasicProfile from "../../../hooks/useBasicProfile";
import useAlert from "../../../hooks/useAlert";
import { getSecrets } from "../../../config";
import { AIAvatarData } from "../../../types/types";
import { set } from "date-fns";
import { useWhiteboardRoom } from "../../../hooks/useWhiteboardRoom";

const AIAvatarDisplay = ({
  imageData,
  isOpen,
  alertContent,
  isError,
  isGenerating,
}: {
  imageData: AIAvatarData;
  isOpen: boolean;
  alertContent: string;
  isError: boolean;
  isGenerating: boolean;
}) => {
  const theme = useTheme();
  const { t, i18n } = useTranslation();
  const currentLanguage =
    !!i18n.language && i18n.language.length > 0 && i18n.language !== "en-US"
      ? i18n.language
      : "zh-TW";
  const { IMGPROXY_BASE_URL } = getSecrets();
  const displayImageData = useMemo(() => {
    if (!imageData) {
      return null;
    }
    try {
      const localeMetadata = imageData.metadata.filter(
        (metadata) => metadata.language === currentLanguage
      )[0];
      if (!localeMetadata) {
        return null;
      }
      return {
        ...imageData,
        localeMetadata,
      };
    } catch (e) {
      console.log("error: ", e);
    }
    return null;
  }, [imageData, currentLanguage]);

  if (!displayImageData) {
    return null;
  }

  const renderPlaceholder = (width = "100%", height = "16px") => (
    <div
      className="ai-avatar-placeholder"
      style={{
        width: width,
        height: height,
      }}
    >
      <div
        className="ai-avatar-placeholder-shimmer"
        style={{
          width: "100%",
          height: "100%",
          background:
            "linear-gradient(90deg, rgba(255,255,255,0) 0%, rgba(255,255,255,0.5) 50%, rgba(255,255,255,0) 100%)",
          animation: "shimmer 1.5s infinite",
          position: "absolute",
          top: 0,
          left: 0,
        }}
      ></div>
    </div>
  );
  const renderImagePlaceholder = () => (
    <div
      style={{
        width: "100%",
        height: "300px",
        backgroundColor: "rgba(255, 255, 255, 0.12)",
        borderRadius: "4px",
        overflow: "hidden",
        position: "relative",
      }}
    >
      <div
        className="ai-avatar-placeholder-shimmer"
        style={{
          width: "100%",
          height: "100%",
          background:
            "linear-gradient(90deg, rgba(255,255,255,0) 0%, rgba(255,255,255,0.5) 50%, rgba(255,255,255,0) 100%)",
          animation: "shimmer 1.5s infinite",
          position: "absolute",
          top: 0,
          left: 0,
        }}
      ></div>
    </div>
  );

  return (
    <>
      <Snackbar
        open={isOpen}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          variant="filled"
          severity={isError ? "error" : "success"}
          color={isError ? "error" : "info"}
        >
          <strong>{alertContent}</strong>
        </Alert>
      </Snackbar>
      <Grid container>
        {isGenerating ? (
          renderImagePlaceholder()
        ) : (
          <img
            style={{ width: "100%", height: "auto" }}
            src={`${IMGPROXY_BASE_URL}/${512}x${512}/${"webp"}/${
              imageData.image_path
            }`}
            alt="AI Avatar"
          />
        )}
      </Grid>
      <div className="ai-avatar-info-container">
        <Grid
          sx={{
            margin: "0px 0",
            flexDirection: "row",
            justifyContent: "start",
            border: "1px solid rgba(255, 255, 255, 0.12)",
            padding: "1rem",
            fontSize: "16px",
            display: "flex",
            alignItems: "center",
            gap: "8px",
            fontWeight: "800",
          }}
          container
        >
          <AutoAwesomeOutlinedIcon fontSize="small" />
          {t("AIAvatar.Display.BasicInfo")}
        </Grid>
        <Grid
          sx={{
            margin: "0px 0",
            flexDirection: "column",
            justifyContent: "center",
            border: "1px solid rgba(255, 255, 255, 0.12)",
            padding: "20px 16px",
            gap: "16px",
          }}
          container
        >
          <div className="ai-avatar-display-info-row-inline">
            <div className="ai-avatar-display-info-row-inline-title">
              {t("AIAvatar.Display.Category")}
              <Tooltip
                title={t("AIAvatar.Tooltip.Category")}
                disableInteractive
                followCursor
              >
                <InfoOutlinedIcon fontSize="small" />
              </Tooltip>
            </div>
            <span className="ai-avatar-display-info-text-big">
              {isGenerating
                ? renderPlaceholder("100px")
                : displayImageData.localeMetadata.locale_category}
            </span>
          </div>
          <div className="ai-avatar-display-info-row-inline">
            <div className="ai-avatar-display-info-row-inline-title">
              {t("AIAvatar.Display.Level")}
              <Tooltip
                title={t("AIAvatar.Tooltip.Level")}
                disableInteractive
                followCursor
              >
                <InfoOutlinedIcon fontSize="small" />
              </Tooltip>
            </div>
            <span>
              {isGenerating
                ? renderPlaceholder("100px")
                : displayImageData.level.split("level")[1]}
            </span>
          </div>
        </Grid>
      </div>
      <div className="ai-avatar-info-container">
        <Grid
          sx={{
            margin: "0px 0",
            flexDirection: "row",
            justifyContent: "start",
            border: "1px solid rgba(255, 255, 255, 0.12)",
            padding: "1rem",
            fontSize: "16px",
            display: "flex",
            alignItems: "center",
            gap: "8px",
            fontWeight: "800",
          }}
          container
        >
          <LabelIcon fontSize="small" />
          {t("AIAvatar.Display.Traits")}
        </Grid>
        <Grid
          sx={{
            margin: "0px 0",
            flexDirection: "column",
            justifyContent: "center",
            border: "1px solid rgba(255, 255, 255, 0.12)",
          }}
          container
        >
          <div className="ai-avatar-display-info-row">
            {t("AIAvatar.Display.Accessories")}
            <span>
              {isGenerating
                ? renderPlaceholder("50px")
                : displayImageData.localeMetadata.accessories != null &&
                  !!displayImageData.localeMetadata.accessories
                ? displayImageData.localeMetadata.accessories
                : t("AIAvatar.Display.None")}
            </span>
          </div>
          <div className="ai-avatar-display-info-row">
            {t("AIAvatar.Display.Actions")}
            <span>
              {isGenerating
                ? renderPlaceholder("50px")
                : displayImageData.localeMetadata.actions}
            </span>
          </div>
          <div className="ai-avatar-display-info-row">
            {t("AIAvatar.Display.ArtStyle")}
            <span>
              {isGenerating
                ? renderPlaceholder("100px")
                : displayImageData.localeMetadata.art_style}
            </span>
          </div>
          <div className="ai-avatar-display-info-row">
            {t("AIAvatar.Display.Background")}
            <span>
              {isGenerating
                ? renderPlaceholder("100px")
                : displayImageData.localeMetadata.background}
            </span>
          </div>
          <div className="ai-avatar-display-info-row">
            {t("AIAvatar.Display.Species")}
            <span>
              {isGenerating
                ? renderPlaceholder("50px")
                : displayImageData.localeMetadata.species}
            </span>
          </div>
        </Grid>
      </div>
      <div className="ai-avatar-info-container">
        <Grid
          sx={{
            margin: "0px 0",
            flexDirection: "row",
            justifyContent: "start",
            border: "1px solid rgba(255, 255, 255, 0.12)",
            padding: "1rem",
            fontSize: "16px",
            display: "flex",
            alignItems: "center",
            gap: "8px",
            fontWeight: "800",
          }}
          container
        >
          <SegmentOutlinedIcon fontSize="small" />
          {t("AIAvatar.Display.Desc")}
        </Grid>
        <Grid
          sx={{
            margin: "0px 0",
            flexDirection: "column",
            justifyContent: "center",
            border: "1px solid rgba(255, 255, 255, 0.12)",
          }}
          container
        >
          <div className="ai-avatar-display-info-row">
            {t("AIAvatar.Display.Name")}
            <span>
              {isGenerating
                ? renderPlaceholder("50px")
                : displayImageData.localeMetadata.name}
            </span>
          </div>
          <div className="ai-avatar-display-info-row">
            {t("AIAvatar.Display.Story")}
            <span className="ai-avatar-display-info-row-description">
              {isGenerating
                ? renderPlaceholder("100%", "150px")
                : displayImageData.localeMetadata.story}
            </span>
          </div>
          <div className="ai-avatar-display-info-row">
            {t("AIAvatar.Display.Abilities")}
            <span className="ai-avatar-display-info-row-description">
              {isGenerating
                ? renderPlaceholder("100%", "150px")
                : displayImageData.localeMetadata.abilities}
            </span>
          </div>
        </Grid>
      </div>
    </>
  );
};

interface AIAvatarPageProps {
  onClose: () => void;
  aiavatar?: AIAvatarData;
  isEditable?: boolean;
}

const AIAvatarPage: React.FC<AIAvatarPageProps> = ({
  onClose,
  aiavatar,
  isEditable,
}) => {
  const theme = useTheme();
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const uuid = getProticoClient().userInfo?.uuid;
  const { ASSETS_BASE_URL } = getSecrets();
  const { roomDomain } = useWhiteboardRoom();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isApplying, setIsApplying] = useState<boolean>(false);
  const [isGenerating, setIsGenerating] = useState<boolean>(false);
  const [pointNotEnough, setPointNotEnough] = useState<boolean>(false);
  const [isApplied, setIsApplied] = useState<boolean>(false);
  const [totalPoints, setTotalPoints] = useState<number>(0);
  const [imageData, setImageData] = useState<AIAvatarData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const { currentAddress } = useAppSelector((state) => state.auth);
  const { refetch } = useBasicProfile(currentAddress);
  const [isOpen, alertContent, isError, setOptionalAlertContent] = useAlert();

  const handleInit = async () => {
    setIsLoading(true);
    setError(null);
    const reward_response = await getProticoClient().engagement.getReward(
      roomDomain
    );
    const total_points = reward_response.total;
    setTotalPoints(total_points);
    if (total_points < 100) {
      setPointNotEnough(true);
    }
    if (!!aiavatar) {
      setImageData(aiavatar);
      setIsLoading(false);
      return;
    }

    try {
      const response = await getProticoClient().api.get(
        "/api/ai_avatar/generate"
      );

      const data = response.data;

      if (!data || !data.image_path) {
        throw new Error(t("AIAvatar.Errors.InvalidData"));
      }

      setImageData(data);
    } catch (err: any) {
      console.error("AIAvatar init failed:", err);
      if (err?.response?.status === 404) {
        setIsLoading(false);
        return;
      }
      setError(
        err?.response?.data?.error ||
          err?.message ||
          t("AIAvatar.Errors.Generic")
      );
    } finally {
      setIsLoading(false);
    }
  };

  // Function to calculate new analysis
  const handleGenerate = async () => {
    if (!isEditable) {
      return;
    }
    if (!uuid) {
      setError(t("AIAvatar.Errors.NoUserId"));
      return;
    }

    setIsGenerating(true);
    setError(null);
    setIsApplied(false);

    try {
      // 模擬5秒的loading
      await new Promise((resolve) => setTimeout(resolve, 5000));

      const response = await getProticoClient().api.post(
        "/api/ai_avatar/generate"
      );

      const data = response.data;

      if (!data || !data.image_path) {
        throw new Error(t("AIAvatar.Errors.InvalidData"));
      }

      setImageData(data);
      if (totalPoints - 100 < 100) {
        setPointNotEnough(true);
      }
      setTotalPoints(totalPoints - 100);
    } catch (err: any) {
      console.error("AIAvatar generate failed:", err);
      setError(
        err?.response?.data?.error ||
          err?.message ||
          t("AIAvatar.Errors.Generic")
      );
    } finally {
      setIsGenerating(false);
    }
  };

  const handleApply = async () => {
    if (!isEditable) {
      return;
    }
    setIsApplying(true);
    setError(null);
    if (!imageData) {
      setError(t("AIAvatar.Errors.NoImage"));
      return;
    }
    try {
      await getProticoClient().api.post(
        `/api/ai_avatar/registry/${imageData.id}`
      );
    } catch (err: any) {
      console.error("AIAvatar apply failed:", err);
      setError(
        err?.response?.data?.error ||
          err?.message ||
          t("AIAvatar.Errors.Generic")
      );
    }

    try {
      dispatch(
        saveProfilePictureFromUrl(
          `${ASSETS_BASE_URL}/${imageData.image_path}`,
          setOptionalAlertContent,
          refetch
        )
      );
      setIsApplied(true);
    } catch (err: any) {
      console.error("AIAvatar apply failed:", err);
      setError(
        err?.response?.data?.error ||
          err?.message ||
          t("AIAvatar.Errors.Generic")
      );
    } finally {
      setIsApplying(false);
    }
  };

  const renderContent = () => {
    if (isLoading) {
      return (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "400px",
          }}
        >
          <CircularProgress />
        </Box>
      );
    }
    if (!imageData || imageData.image_path.length === 0) {
      return (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            padding: "20px",
            height: "100%",
          }}
        >
          <h4 style={{ margin: "0 0 20px 0" }}>
            {t("AIAvatar.Tooltip.Category")}
          </h4>
          <h4 style={{ margin: "20px 0" }}>{t("AIAvatar.Tooltip.Level")}</h4>
          <Tooltip
            title={pointNotEnough ? t("AIAvatar.Tooltip.PointNotEnough") : ""}
          >
            <span>
              <Button
                variant="contained"
                onClick={handleGenerate}
                disabled={isGenerating || pointNotEnough}
                sx={{
                  minWidth: "200px",
                  backgroundColor: "#1f8feb",
                  color: "white",
                  marginTop: "20px",
                }}
              >
                {isGenerating ? (
                  <>
                    <CircularProgress size={16} sx={{ mr: 1, color: "#777" }} />
                    {t("AIAvatar.Generating")}
                  </>
                ) : (
                  t("AIAvatar.Generate")
                )}
              </Button>
            </span>
          </Tooltip>
        </Box>
      );
    }
    return (
      <AIAvatarDisplay
        isGenerating={isGenerating}
        imageData={imageData}
        isOpen={isOpen}
        alertContent={alertContent}
        isError={isError}
      />
    );
  };

  useEffect(() => {
    handleInit();
  }, []);

  return (
    <Dialog
      open={true}
      onClose={onClose}
      fullScreen
      PaperProps={{
        sx: {
          bgcolor: "rgb(4 4 4)",
          color: theme.palette.text.primary,
          margin: "0px",
          position: "relative", // 确保定位生效
        },
      }}
    >
      <IconButton
        aria-label="close"
        onClick={onClose}
        sx={{
          position: "absolute", // 绝对定位
          top: "8px", // 距离顶部
          left: "8px", // 距离左侧
          color: theme.palette.text.primary,
          zIndex: 1, // 确保按钮在最上层
        }}
      >
        <CloseIcon />
      </IconButton>
      <DialogTitle
        sx={{
          bgcolor: "rgb(4 4 4)",
          fontSize: "16px",
          textAlign: "center",
          padding: "16px 24px 16px 32px",
        }}
      >
        {t("AIAvatar.Title")}
      </DialogTitle>
      <DialogContent sx={{ bgcolor: "rgb(4 4 4)", padding: "8px" }}>
        {error && (
          <Alert severity="error" sx={{ position: "absolute", top: "50px" }}>
            {error}
          </Alert>
        )}
        {renderContent()}
        {isEditable && !!imageData && (
          <div className="ai-avatar-apply-button-container">
            <Button
              variant="contained"
              onClick={handleApply}
              disabled={isApplied || !imageData}
              sx={{
                minWidth: "200px",
                backgroundColor: "#1f8feb",
                color: "white",
              }}
            >
              {isApplied ? (
                <>{t("AIAvatar.ApplySuccess")}</>
              ) : (
                t("AIAvatar.Apply")
              )}
            </Button>
            <Tooltip
              title={pointNotEnough ? t("AIAvatar.Tooltip.PointNotEnough") : ""}
            >
              <span>
                <Button
                  variant="contained"
                  onClick={handleGenerate}
                  disabled={isGenerating || pointNotEnough}
                  sx={{
                    minWidth: "200px",
                    backgroundColor: "#ff9740",
                    color: "white",
                    width: "-webkit-fill-available",
                  }}
                >
                  {isGenerating ? (
                    <>
                      <CircularProgress size={20} sx={{ mr: 1 }} />
                      {t("AIAvatar.Generating")}
                    </>
                  ) : (
                    t("AIAvatar.ReGenerate")
                  )}
                </Button>
              </span>
            </Tooltip>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default AIAvatarPage;
